import React, { useEffect } from 'react'
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Link,
  Button
} from '@chakra-ui/react'
import { useStoreActions, useStoreState } from 'easy-peasy'
import BillingContactModal from './components/billing-contact-modal'
import { Select as JoySelect } from '@blueprinthq/joy'
import { Skeleton } from '@chakra-ui/react'
import { Select } from '@chakra-ui/react'
import { useQuery } from 'react-query'
import { endpoints } from '@api'
import { usePermissions, useExperienceManager } from '@hooks'
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom'
import moment from 'moment'
import PaymentMethodModal from './components/payment-method-modal'
import { BillingTable } from './components/billing-table'
import { MicAIOn, EvidenceBasedCare } from '@components/icons'

export const BillingTab = ({}) => {
  const { user } = useStoreState(state => state.auth)
  const openDAModal = useStoreActions(
    actions => actions.modals.documentationAutomationMarketingModal.openModal
  )
  const location = useLocation()
  const params = new URLSearchParams(location.search).get('openModal')
  const history = useHistory()
  const { hasPermission } = usePermissions()
  const [selectedClinic, setSelectedClinic] = React.useState(user?.clinic)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false)
  const [amountOfInvoicesToLoad, setAmountOfInvoicesToLoad] = React.useState(12)
  const [totalInvoiceCount, setTotalInvoiceCount] = React.useState(0)
  const { isDocumentationAutomationFreePlan, isAdmin } = useExperienceManager()
  const handleOpenModal = useStoreActions(
    actions => actions.modals.choosePlan.openModal
  )

  const openEbcMarketingModal = useStoreActions(
    actions => actions.modals.ebcMarketingModal.openModal
  )

  const handleOpenDocAutomationPlanModal = useStoreActions(
    actions => actions.modals.chooseDocumentationAutomationPlan.openModal
  )

  const { data: clinics } = useQuery(
    [
      endpoints.getOrganizationClinics.getCacheId(),
      selectedClinic && selectedClinic.organization_id
    ],
    () =>
      endpoints.getOrganizationClinics.request({
        organizationId: user.clinic.organization_id
      }),
    {
      initialData: [],
      enabled: hasPermission('read:org:clinics:list'),
      keepPreviousData: true,
      onSuccess(data) {
        // default to the currently selected clinic, otherwise use the user's clinic
        setSelectedClinic(
          data.find(clinic => clinic.id === selectedClinic?.id) ||
            data.find(clinic => clinic.id === user.clinic.id)
        )
      }
    }
  )

  const {
    data: {
      meta: { totalCount }
    },
    isFetching: isProvidersLoading
  } = useQuery(
    [
      endpoints.getOrganizationProvidersV2.getCacheId(),
      1,
      selectedClinic && selectedClinic.organization_id,
      selectedClinic && selectedClinic.id
    ],
    () =>
      endpoints.getOrganizationProvidersV2.request({
        organizationId: selectedClinic.organization_id,
        clinicId: selectedClinic.id,
        page: 1,
        perPage: 50
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      placeholderData: {
        providers: [],
        meta: {
          page: 1,
          perPage: 50,
          totalCount: 0
        }
      },
      enabled: selectedClinic !== null
    }
  )

  const { data: billing, isFetching: isBillingLoading } = useQuery(
    [
      endpoints.getClinicBilling.getCacheId(),
      selectedClinic && selectedClinic.id
    ],
    () =>
      endpoints.getClinicBilling.request({
        clinicId: selectedClinic.id
      }),
    {
      initialData: [],
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess(data) {
        // default to the currently selected clinic, otherwise use the user's clinic
        return data
      }
    }
  )

  const { data: invoices, isFetching: isInvoicesLoading, refetch } = useQuery(
    [
      endpoints.getClinicInvoices.getCacheId(),
      selectedClinic && selectedClinic.id
    ],
    () =>
      endpoints.getClinicInvoices.request({
        clinicId: selectedClinic.id,
        amountOfInvoicesToLoad: amountOfInvoicesToLoad
      }),
    {
      initialData: [],
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess(data) {
        // default to the currently selected clinic, otherwise use the user's clinic
        return data
      }
    }
  )

  useEffect(() => {
    if (params) {
      setIsPaymentModalOpen(true)
    }
  }, [params])

  useEffect(() => {
    refetch()
  }, [amountOfInvoicesToLoad])

  const onLoadMoreInvoices = async () => {
    setAmountOfInvoicesToLoad(totalInvoiceCount)
  }

  useEffect(() => {
    if (invoices.invoices && invoices.invoices.invoices.length > 0) {
      const { totalCount } = invoices.invoices
      setTotalInvoiceCount(totalCount)
    }
  }, [invoices.invoices])

  function formatDollarAmount(amountStr) {
    // Convert the string to a number
    let amount = parseFloat(amountStr)

    // Check if the conversion was successful
    if (isNaN(amount)) {
      return '0'
    }

    // Determine if the amount has cents
    const hasCents = amount % 1 !== 0

    // Convert the number back to a string with or without decimal places based on the cents
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0
    })
  }

  const handleContactSupport = () => {
    window.Intercom('showNewMessage', "Hi, I'd like to cancel my subscription.")
  }

  useEffect(() => {
    if (!isAdmin) {
      history.push('/dashboard')
    }
  }, [isAdmin])

  return (
    <>
      <Box mb="xlarge">
        <Box>
          <Flex justify="space-between" alignItems="center">
            <Box w={'100%'}>
              <Heading mb="xxsmall">Billing</Heading>
              <Text>Manage your Blueprint subscription</Text>
            </Box>
          </Flex>

          <Flex justify="space-evenly" alignItems="center" w="100%" mt="medium">
            {hasPermission('update:org:clinics:*') && (
              <Box w="100%" mb={'small'}>
                {hasPermission('read:any:clinics:list') ? (
                  <Select
                    value={selectedClinic.id}
                    style={{ border: '1px solid #E4E5E6' }}
                    borderRadius="4px"
                    height="48px"
                    _focus={{
                      outline: 'none'
                    }}
                    onChange={e => {
                      setAmountOfInvoicesToLoad(12)
                      setSelectedClinic(
                        clinics.find(c => c.id === e.target.value)
                      )
                    }}
                  >
                    {clinics.map(c => {
                      return (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      )
                    })}
                  </Select>
                ) : (
                  <JoySelect
                    value={selectedClinic}
                    options={clinics}
                    valueKey="id"
                    labelKey="name"
                    onChange={value => {
                      setAmountOfInvoicesToLoad(12)
                      setSelectedClinic(value)
                    }}
                  />
                )}
              </Box>
            )}
          </Flex>
          {!isBillingLoading &&
            billing.ebcPlans &&
            billing.ebcPlans.length !== 0 && (
              <SimpleGrid
                mb={'small'}
                columns={[1, 1, 5]}
                p={'24px'}
                borderRadius={'4px'}
                border={'1px solid #E4E5E6'}
                display={{ base: '', md: 'flex' }}
                justifyContent={'center'}
                gap={'24px'}
                alignSelf={'stretch'}
              >
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <EvidenceBasedCare
                    fill={'#2D54E8'}
                    width={'40px'}
                    height={'40px'}
                  />
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 0 0'
                  }}
                >
                  <Text fontSize={'16px'}>Plan</Text>
                  {isBillingLoading && <Skeleton height="30px" />}

                  {!isBillingLoading &&
                    billing.ebcPlans.map(ebc => {
                      return (
                        <>
                          <Text fontSize={'16px'} fontWeight={'bold'}>
                            {!isBillingLoading &&
                              ebc.planName &&
                              ebc.planName +
                                ` (${
                                  ebc.billingInterval === 'month'
                                    ? 'Monthly'
                                    : 'Annual'
                                })`}
                          </Text>
                          {!isBillingLoading && (
                            <Text
                              _focus={{ outline: 'none' }}
                              fontSize={'16px'}
                              color={'primary'}
                              _hover={{
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                if (billing?.canChangeEBCPlan) {
                                  handleOpenModal()
                                } else {
                                  window.Intercom(
                                    'showNewMessage',
                                    "Hi, I'd like to change my Evidence-based care plan."
                                  )
                                }
                              }}
                            >
                              {billing?.canChangeEBCPlan
                                ? 'Change plan'
                                : 'Contact support'}
                            </Text>
                          )}
                        </>
                      )
                    })}
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '160px'
                  }}
                >
                  <Text fontSize={'16px'}>Next invoice</Text>
                  <Text fontSize={'16px'} fontWeight={'bold'}>
                    {isBillingLoading && <Skeleton height="30px" />}
                    {!isBillingLoading &&
                      billing.nextDueInvoice &&
                      moment
                        .unix(
                          billing.nextDueInvoice.next_payment_attempt !== null
                            ? billing.nextDueInvoice.next_payment_attempt
                            : billing.nextDueInvoice.period_end
                        )
                        .format('MMM D, YYYY')}
                  </Text>
                </Box>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '160px'
                  }}
                >
                  <Text fontSize={'16px'}>Invoice total</Text>
                  <Text fontSize={'16px'} fontWeight={'bold'}>
                    {isBillingLoading && <Skeleton height="30px" />}
                    {!isBillingLoading &&
                      billing.nextDueInvoice &&
                      formatDollarAmount(
                        billing.nextDueInvoice.amount_due / 100
                      )}
                  </Text>
                </Box>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '160px'
                  }}
                >
                  <Text fontSize={'16px'}>Total users</Text>
                  <Text fontSize={'16px'} fontWeight={'bold'}>
                    {isProvidersLoading ? (
                      <Skeleton height="30px" />
                    ) : (
                      totalCount
                    )}
                  </Text>
                </Box>
              </SimpleGrid>
            )}

          {!isBillingLoading &&
            billing.ebcPlans &&
            billing.ebcPlans.length === 0 && (
              <>
                <SimpleGrid
                  mb={'small'}
                  columns={[1, 1, 3]}
                  p={'24px'}
                  borderRadius={'4px'}
                  border={'1px solid #E4E5E6'}
                  display={{ base: '', md: 'flex' }}
                  justifyContent={'center'}
                  gap={'24px'}
                  alignSelf={'stretch'}
                >
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <EvidenceBasedCare
                      fill={'#2D54E8'}
                      width={'40px'}
                      height={'40px'}
                    />
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1 0 0'
                    }}
                  >
                    <Text fontSize={'16px'} fontWeight={'bold'}>
                      Evidence-Based Care
                    </Text>
                    <Text fontSize={'16px'} fontWeight={430}>
                      Start measuring outcomes and assigning homework between
                      sessions.
                    </Text>
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '220px'
                    }}
                  >
                    <Button onClick={() => openEbcMarketingModal()}>
                      Start a 30 day free trial
                    </Button>
                  </Box>
                </SimpleGrid>
              </>
            )}
          {!isBillingLoading && !billing.activeDocAutomationPlan && (
            <>
              <SimpleGrid
                mb={'small'}
                columns={[1, 1, 3]}
                p={'24px'}
                borderRadius={'4px'}
                border={'1px solid #E4E5E6'}
                display={{ base: '', md: 'flex' }}
                justifyContent={'center'}
                gap={'24px'}
                alignSelf={'stretch'}
              >
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <MicAIOn fill={'#2D54E8'} width={'40px'} height={'40px'} />
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 0 0'
                  }}
                >
                  <Text fontSize={'16px'} fontWeight={'bold'}>
                    Documentation Automation
                  </Text>
                  <Text fontSize={'16px'} fontWeight={430}>
                    Use an audio recorder during session to automate progress
                    notes.
                  </Text>
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '220px'
                  }}
                >
                  <Button onClick={() => openDAModal()}>Try for free</Button>
                </Box>
              </SimpleGrid>
            </>
          )}

          {!isBillingLoading && billing.activeDocAutomationPlan && (
            <SimpleGrid
              mb={'small'}
              columns={[1, 1, 5]}
              p={'24px'}
              borderRadius={'4px'}
              border={'1px solid #E4E5E6'}
              display={{ base: '', md: 'flex' }}
              justifyContent={'center'}
              gap={'24px'}
              alignSelf={'stretch'}
            >
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <MicAIOn fill={'#2D54E8'} width={'40px'} height={'40px'} />
              </Box>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 0 0'
                }}
              >
                <Text fontSize={'16px'}>Plan</Text>
                <Text fontSize={'16px'} fontWeight={'bold'}>
                  {isBillingLoading && <Skeleton height="30px" />}
                  {!isBillingLoading &&
                    billing.activeDocAutomationPlan &&
                    billing.activeDocAutomationPlan.planName +
                      ` (${
                        billing.activeDocAutomationPlan.billingInterval ===
                        'month'
                          ? 'Monthly'
                          : 'Annual'
                      })`}
                </Text>
                {!isBillingLoading && (
                  <Text
                    _focus={{ outline: 'none' }}
                    fontSize={'16px'}
                    color={'primary'}
                    _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => {
                      if (isDocumentationAutomationFreePlan) {
                        handleOpenDocAutomationPlanModal()
                      } else {
                        window.Intercom(
                          'showNewMessage',
                          "Hi, I'd like to change my Documentation Automation plan."
                        )
                      }
                    }}
                  >
                    {isDocumentationAutomationFreePlan
                      ? 'Upgrade'
                      : 'Contact support'}
                  </Text>
                )}
              </Box>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '160px'
                }}
              >
                <Text fontSize={'16px'}>Next invoice</Text>
                <Text fontSize={'16px'} fontWeight={'bold'}>
                  {isBillingLoading && <Skeleton height="30px" />}
                  {!isBillingLoading &&
                    billing.nextDueDocAutomationInvoice &&
                    moment
                      .unix(
                        billing.nextDueDocAutomationInvoice
                          .next_payment_attempt !== null
                          ? billing.nextDueDocAutomationInvoice
                              .next_payment_attempt
                          : billing.nextDueDocAutomationInvoice.period_end
                      )
                      .format('MMM D, YYYY')}
                </Text>
              </Box>

              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '160px'
                }}
              >
                <Text fontSize={'16px'}>Invoice total</Text>
                <Text fontSize={'16px'} fontWeight={'bold'}>
                  {isBillingLoading && <Skeleton height="30px" />}
                  {!isBillingLoading &&
                    billing.nextDueDocAutomationInvoice &&
                    formatDollarAmount(
                      billing.nextDueDocAutomationInvoice.amount_due / 100
                    )}
                </Text>
              </Box>

              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '160px'
                }}
              >
                <Text fontSize={'16px'}>Notes used</Text>
                <Text fontSize={'16px'} fontWeight={'bold'}>
                  {isProvidersLoading ? (
                    <Skeleton height="30px" />
                  ) : (
                    billing.activeDocAutomationPlan.sessionsUsed +
                    '/' +
                    billing.activeDocAutomationPlan.sessionsAllotted
                  )}
                </Text>
              </Box>
            </SimpleGrid>
          )}

          <SimpleGrid
            columns={[1, 1, 2]}
            p={'24px'}
            borderRadius={'4px'}
            border={'1px solid #E4E5E6'}
          >
            <Box>
              <Text fontSize={'16px'}>Invoices sent to</Text>
              <Text fontSize={'16px'} fontWeight={'bold'}>
                {isBillingLoading && <Skeleton height="30px" width="218px" />}
                {!isBillingLoading &&
                  billing.nextDueInvoice &&
                  billing.nextDueInvoice.customer_email}
              </Text>
              {!isBillingLoading && (
                <Text
                  _focus={{ outline: 'none' }}
                  fontSize={'16px'}
                  color={'primary'}
                  _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setIsModalOpen(true)}
                >
                  Change contact
                </Text>
              )}
            </Box>
            <Box>
              <Text fontSize={'16px'}>Payment method</Text>
              <Text fontSize={'16px'} fontWeight={'bold'}>
                {isBillingLoading && <Skeleton height="30px" width="218px" />}
                {!isBillingLoading && billing && billing.card && (
                  <>
                    <Text
                      display={'inline'}
                      fontSize={'16px'}
                      textTransform={'capitalize'}
                      fontWeight={'bold'}
                    >
                      {billing.card.card.brand}
                    </Text>{' '}
                    {`ending in **** ${billing.card.card.last4}`}
                  </>
                )}
              </Text>

              {!isBillingLoading && (
                <Text
                  _focus={{ outline: 'none' }}
                  fontSize={'16px'}
                  color={'primary'}
                  _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  Change payment method
                </Text>
              )}
            </Box>
          </SimpleGrid>
          <Box mt={'40px'} w={'100%'}>
            <Heading fontSize={'16px'} mb="xxsmall">
              Invoices
            </Heading>
          </Box>
          <BillingTable
            billing={invoices}
            isBillingLoading={isInvoicesLoading}
            loadAll={() => onLoadMoreInvoices()}
            hasMore={invoices?.invoices?.hasMore}
          />
          {!isBillingLoading && (
            <Box
              w="100%"
              border="1px solid"
              borderColor="#E4E5E6"
              borderRadius="4px"
              mt={'40px'}
              mb={'small'}
              bg="white"
              p={'24px'}
              verticalAlign={'middle'}
            >
              <Text fontSize={'16px'}>
                {' '}
                Want to permanently cancel your Blueprint account?{' '}
                {!billing.canCancel ? (
                  <Link color="#EB5164" onClick={handleContactSupport}>
                    Contact support
                  </Link>
                ) : (
                  <Link as={RouterLink} color="#EB5164" to="/cancel">
                    Cancel your account
                  </Link>
                )}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
      {!isBillingLoading && billing && (
        <BillingContactModal
          isModalOpen={isModalOpen}
          handleModal={() => setIsModalOpen(false)}
          user_id={
            !isBillingLoading && billing.nextDueInvoice
              ? billing.nextDueInvoice.customer
              : ''
          }
          selectedClinic={selectedClinic}
          currentEmail={
            billing.nextDueInvoice && billing.nextDueInvoice.customer_email
          }
        />
      )}
      {!isBillingLoading && billing && (
        <PaymentMethodModal
          isModalOpen={isPaymentModalOpen}
          handleModal={() => setIsPaymentModalOpen(false)}
          user_id={
            !isBillingLoading && billing.nextDueInvoice
              ? billing.nextDueInvoice.customer
              : ''
          }
          sub_id={
            !isBillingLoading && billing.nextDueInvoice
              ? billing.nextDueInvoice.subscription
              : ''
          }
          selectedClinic={selectedClinic}
          invoices={
            !isBillingLoading && !isInvoicesLoading && invoices.invoices
              ? invoices.invoices.invoices
              : []
          }
        />
      )}
    </>
  )
}
